import { useSessionStore } from '@/entities/session';
import { cn } from '@/shared/lib/cn';
import { Button, Typography } from '@/shared/ui';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { type FC, useEffect } from 'react';
import type { PeriodSelectorState, ShortDatePeriod } from '../model/types';

type PeriodSelectorProps = {
  include: Array<ShortDatePeriod | 'from' | 'to'>;
  defaultShortDatePeriod?: ShortDatePeriod;
  value: PeriodSelectorState;
  disabled?: boolean;
  monthsOnly?: boolean;
  /**
   * Renders for the blue app header instead of a white page background:
   * white borders/labels, primary-6 fills, muted placeholder. Selected period
   * inverts to a white chip.
   */
  onDark?: boolean;
  onChange: (periodSelectorState: PeriodSelectorState) => void;
};

const MONTHS_IN_PERIOD: Record<ShortDatePeriod, number> = {
  '1m': 1,
  '6m': 6,
  '1y': 12,
};

/**
 * `monthsOnly` re-bases the chips onto whole *completed* months instead of a
 * rolling window ending today: 1M is last month, 6M the last six months, 1Y the
 * last twelve — every one of them ending on the last completed month.
 *
 * Without this, 1M on the 13th would select the 14th of last month through
 * today, i.e. two partial months including the running one — which the Financial
 * tab must never show (ST #137).
 */
export const calculatePeriodDateRange = (
  period: ShortDatePeriod,
  monthsOnly = false
) => {
  if (monthsOnly) {
    const to = dayjs().subtract(1, 'month').endOf('month');
    const from = to
      .startOf('month')
      .subtract(MONTHS_IN_PERIOD[period] - 1, 'month');

    return { from, to };
  }

  const to = dayjs();
  let from: dayjs.Dayjs;

  switch (period) {
    case '1m':
      from = to.subtract(1, 'month').add(1, 'day');
      break;
    case '6m':
      from = to.subtract(6, 'month').add(1, 'day');
      break;
    case '1y':
      from = to.subtract(1, 'year').add(1, 'day');
      break;
  }

  return { from, to };
};

export const PeriodSelector: FC<PeriodSelectorProps> = ({
  include,
  defaultShortDatePeriod = '1m',
  value,
  disabled = false,
  monthsOnly = false,
  onDark = false,
  onChange,
}) => {
  const minDate = useSessionStore(
    (state) =>
      state.user?.earliestSnapshotDate ??
      state.user?.emrIntegration.createdAt ??
      undefined
  );

  const rendered = value.renderedDateRange ?? {};
  const dateRange =
    value.dateRange ??
    calculatePeriodDateRange(defaultShortDatePeriod, monthsOnly);

  const renderedEmpty =
    !value?.renderedDateRange ||
    Object.keys(value.renderedDateRange).length === 0;

  const selectedPeriod: ShortDatePeriod | undefined =
    (rendered.period as ShortDatePeriod | undefined) ??
    (renderedEmpty && include.includes(defaultShortDatePeriod)
      ? defaultShortDatePeriod
      : undefined);
  const fromSelected = Boolean(rendered.from);
  const toSelected = Boolean(rendered.to);

  const selectedFrom =
    rendered.from ??
    dateRange.from ??
    calculatePeriodDateRange(defaultShortDatePeriod, monthsOnly).from;
  const selectedTo =
    rendered.to ??
    dateRange.to ??
    calculatePeriodDateRange(defaultShortDatePeriod, monthsOnly).to;

  // Picking 1M/6M/1Y computes a concrete range, so show it in the From/To
  // fields rather than leaving them on "Select date" — the range is active
  // either way, and an empty field reads as "no range applied".
  const showFromDate = fromSelected || Boolean(selectedPeriod);
  const showToDate = toSelected || Boolean(selectedPeriod);

  useEffect(() => {
    const renderedEmpty =
      !value?.renderedDateRange ||
      Object.keys(value.renderedDateRange).length === 0;

    if (!renderedEmpty) return;

    if (include.includes(defaultShortDatePeriod)) {
      const { from, to } = calculatePeriodDateRange(
        defaultShortDatePeriod,
        monthsOnly
      );

      onChange({
        dateRange: { from, to },
        renderedDateRange: { period: defaultShortDatePeriod },
      });
    }
  }, [include, defaultShortDatePeriod, monthsOnly, value]);

  // On dark we drive the selected/unselected chip explicitly: Button's `active`
  // classes (text-primary-7 / border-primary-7) are tuned for a white page.
  //
  // Everything here is `primary-*`, never `neutral-*`: the header is brand blue
  // in light *and* dark mode, so a chip sitting on it must look identical in
  // both. Using neutral-1 for the selected chip's fill made it flip to a dark
  // grey in dark mode, leaving navy `text-primary-6` on it — invisible.
  const periodButtonClass = (isSelected: boolean) =>
    cn(
      'h-6 w-6 p-0',
      onDark &&
        (isSelected
          ? 'bg-primary-inverse text-primary-6 border-primary-inverse'
          : 'bg-primary-6 text-primary-inverse border-primary-inverse hover:text-primary-inverse hover:border-primary-inverse'),
      // Button's shared disabled styles paint a light-grey chip, which reads as
      // a foreign element on the blue header. The design keeps the fill at
      // primary-6 and just mutes the border and label (Figma 2:8483).
      onDark &&
        disabled &&
        'disabled:bg-primary-6 disabled:text-primary-disabled disabled:border-primary-disabled'
    );

  const datePickerClass = cn('h-10', onDark && 'period-selector-on-dark');

  // The From:/To: labels sit outside the antd picker, so they keep their full
  // white unless dimmed here -- which made a disabled control still read as
  // usable.
  const dateLabelClass = cn(
    onDark && (disabled ? 'text-primary-disabled' : 'text-primary-inverse')
  );

  const isAnyPeriodIncluded = include.some(
    (d) => d === '1m' || d === '6m' || d === '1y'
  );
  const isSpecificDateIncluded = include.some(
    (d) => d === 'from' || d === 'to'
  );

  const computeDateRangeFromRendered = (
    r: PeriodSelectorState['renderedDateRange']
  ) => {
    let from = r.from;
    let to = r.to;

    if (r.period) {
      const p = calculatePeriodDateRange(r.period, monthsOnly);
      from = p.from;
      to = p.to;
    }

    // A months-only range is always whole months, so an open end means "through
    // the last completed month", never "through today".
    if (from && !to) {
      to = monthsOnly ? dayjs().subtract(1, 'month').endOf('month') : dayjs();
    }

    if (to && !from) {
      from = minDate ? dayjs(minDate) : dayjs();
    }

    // The month picker hands back the 1st of the picked month for both fields,
    // which would make "Jul to Jul" a single-day range. Widen to whole months so
    // the emitted range always covers the months the user sees.
    if (monthsOnly) {
      return { from: from!.startOf('month'), to: to!.endOf('month') };
    }

    return { from: from!, to: to! };
  };
  const updateRendered = (
    newRendered: PeriodSelectorState['renderedDateRange']
  ) => {
    const dateRangeToEmit = computeDateRangeFromRendered(newRendered);
    onChange({ dateRange: dateRangeToEmit, renderedDateRange: newRendered });
  };
  const selectPeriod = (period: ShortDatePeriod) => updateRendered({ period });
  const handleFromChange = (date?: dayjs.Dayjs) => {
    if (!date) return;

    const newRendered: PeriodSelectorState['renderedDateRange'] = {
      from: date,
    };
    if (toSelected && rendered.to) newRendered.to = rendered.to;
    updateRendered(newRendered);
  };
  const handleToChange = (date?: dayjs.Dayjs) => {
    if (!date) return;

    const newRendered: PeriodSelectorState['renderedDateRange'] = { to: date };
    if (fromSelected && rendered.from) newRendered.from = rendered.from;
    updateRendered(newRendered);
  };

  return (
    <div className="flex items-center gap-4">
      {isAnyPeriodIncluded && (
        <div className="flex items-center gap-2">
          {include.includes('1m') && (
            <Button
              variant="secondary"
              disabled={disabled}
              active={selectedPeriod === '1m'}
              className={periodButtonClass(selectedPeriod === '1m')}
              onClick={() => selectPeriod('1m')}
            >
              1M
            </Button>
          )}
          {include.includes('6m') && (
            <Button
              variant="secondary"
              disabled={disabled}
              active={selectedPeriod === '6m'}
              className={periodButtonClass(selectedPeriod === '6m')}
              onClick={() => selectPeriod('6m')}
            >
              6M
            </Button>
          )}
          {include.includes('1y') && (
            <Button
              variant="secondary"
              disabled={disabled}
              active={selectedPeriod === '1y'}
              className={periodButtonClass(selectedPeriod === '1y')}
              onClick={() => selectPeriod('1y')}
            >
              1Y
            </Button>
          )}
        </div>
      )}

      {isSpecificDateIncluded && (
        <div className="flex items-center gap-2">
          {include.includes('from') && (
            <div className="flex items-center gap-1">
              <Typography className={dateLabelClass}>From:</Typography>
              <DatePicker
                allowClear={false}
                disabled={disabled}
                className={datePickerClass}
                picker={monthsOnly ? 'month' : 'date'}
                format={monthsOnly ? 'MMM YYYY' : 'MM/DD/YYYY'}
                value={showFromDate ? selectedFrom : undefined}
                disabledDate={(date) => {
                  const isCurrentMonth =
                    monthsOnly && date.isSame(dayjs(), 'month');
                  const afterToday = date.isAfter(dayjs(), 'day');
                  const afterSelectedTo =
                    toSelected && rendered.to
                      ? date.isAfter(rendered.to, 'day')
                      : false;

                  if (!minDate)
                    return isCurrentMonth || afterToday || afterSelectedTo;

                  const beforeMin = date.isBefore(
                    dayjs(minDate).subtract(1, 'day')
                  );
                  return (
                    isCurrentMonth || beforeMin || afterToday || afterSelectedTo
                  );
                }}
                onChange={handleFromChange}
              />
            </div>
          )}

          {include.includes('to') && (
            <div className="flex items-center gap-1">
              <Typography className={dateLabelClass}>To:</Typography>
              <DatePicker
                allowClear={false}
                disabled={disabled}
                className={datePickerClass}
                picker={monthsOnly ? 'month' : 'date'}
                value={showToDate ? selectedTo : undefined}
                format={monthsOnly ? 'MMM YYYY' : 'MM/DD/YYYY'}
                disabledDate={(date) => {
                  const isCurrentMonth =
                    monthsOnly && date.isSame(dayjs(), 'month');
                  const afterToday = date.isAfter(dayjs(), 'day');
                  const beforeSelectedFrom =
                    fromSelected && rendered.from
                      ? date.isBefore(rendered.from, 'day')
                      : false;

                  if (!minDate)
                    return isCurrentMonth || beforeSelectedFrom || afterToday;

                  const beforeMin = date.isBefore(
                    dayjs(minDate).subtract(1, 'day')
                  );
                  return (
                    isCurrentMonth ||
                    beforeMin ||
                    beforeSelectedFrom ||
                    afterToday
                  );
                }}
                onChange={handleToChange}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
